import { Card } from 'shared/components';
import { FormWithValidation } from 'shared/form';
import { Button } from '@orbita-ui/core';
import { Col, Row } from 'react-bootstrap';
import { isEmail } from 'shared/validations/ValidateEmail';
import { useForm, useWatch } from 'react-hook-form';
import { FormLabel } from '../FormLabel';
import { useContext, useEffect, useRef } from 'react';
import { CENTRO_COSTO, TARJETA, TIPO_USUARIO } from 'user/constants/Funcionalidad';
import { FuncionalidadesSelector } from '../FuncionalidadesSelector/FuncionalidadesSelector';
import { TipoUsuario, Usuario } from 'user/models/Usuario';
import { UserInfoContext } from 'user/context/UserInfoContext';
import { FORM_ERROR_MESSAGES } from 'shared/form/constants/form-constants';
import { SAVE_USER_ERROR_MESSAGES } from 'user/constants/User';
import { UserRequest } from 'user/models/UserRequest';
import { UserFormSkeleton } from './UserFormSkeleton';
import { SelectOption } from 'shared/form/models/SelectOption';
import { TarjetaSelector } from 'core/tarjeta/components/TarjetaSelector/TarjetaSelector';
import { CuentaSelector } from 'core/cuenta/components/CuentaSelector/CuentaSelector';

export interface UserFormValue {
  codigo: string;
  nombre: string;
  mail: string;
  tipoUsuario: SelectOption<TipoUsuario>;
  numeroCuenta: string;
  tarjetaId: number | null;
  funcionalidades: string[];
}

function mapFormValues(user: Usuario): UserFormValue {
  const tipoUsuario = TIPO_USUARIO.find((item) => item.value === user.tipoUsuario);
  return {
    codigo: user.username,
    nombre: user.detalle,
    mail: user.mail,
    tipoUsuario,
    numeroCuenta: `${user.recursoControlado.numeroCuenta}`,
    tarjetaId: user.tipoUsuario === TARJETA ? (user.recursoControlado.id ?? null) : null,
    funcionalidades: user.funcionalidades,
  };
}

export function UserForm() {
  const methods = useForm<UserFormValue>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  const { userInfo, isLoading, apiErrors, saveUser, isCreationPage } = useContext(UserInfoContext);

  const initialCuentaRef = useRef<string | null>(null);
  const formInitializedRef = useRef(false);

  const { reset, setValue, setError, control, handleSubmit, trigger } = methods;

  const selectedCuenta = useWatch({ control, name: 'numeroCuenta' });
  const tipoUsuario = useWatch({ control, name: 'tipoUsuario' });

  useEffect(() => {
    if (isCreationPage) {
      reset({
        codigo: '',
        nombre: '',
        mail: '',
        tipoUsuario: null,
        numeroCuenta: null,
        tarjetaId: null,
        funcionalidades: [],
      });

      initialCuentaRef.current = null;
      formInitializedRef.current = false;
      return;
    }

    if (userInfo) {
      const formValue = mapFormValues(userInfo);

      reset(formValue);

      initialCuentaRef.current = formValue.numeroCuenta;
      formInitializedRef.current = true;
    }
  }, [isCreationPage, userInfo, reset]);

  useEffect(() => {
    const isEditing = !isCreationPage;
    const isTarjetaType = tipoUsuario?.value === TARJETA;

    if (
      isEditing &&
      isTarjetaType &&
      selectedCuenta &&
      initialCuentaRef.current &&
      selectedCuenta !== initialCuentaRef.current
    ) {
      setValue('tarjetaId', null, { shouldValidate: true });
    }
  }, [selectedCuenta, tipoUsuario?.value, isCreationPage, setValue]);

  useEffect(() => {
    if (!apiErrors) return;

    const errorMappings = {
      user_exist: { field: 'codigo', message: SAVE_USER_ERROR_MESSAGES.user_exist },
      cost_centre_not_found: {
        field: 'numeroCuenta',
        message: SAVE_USER_ERROR_MESSAGES.cost_centre_not_found,
      },
      card_not_found: {
        field: 'tarjetaId',
        message: SAVE_USER_ERROR_MESSAGES.card_not_found,
      },
      invalid_email: { field: 'mail', message: FORM_ERROR_MESSAGES.invaildEmail },
    };

    Object.keys(errorMappings).forEach((key) => {
      if (apiErrors[key]) {
        setError(errorMappings[key].field as any, {
          type: 'custom',
          message: errorMappings[key].message,
        });
      }
    });
  }, [apiErrors, setError]);

  const handleChangeTipoUsuario = (option: SelectOption<TipoUsuario>) => {
    setValue('tipoUsuario', option, { shouldDirty: true });
    setValue('numeroCuenta', null);
    setValue('tarjetaId', null);
    setValue('funcionalidades', null);
    initialCuentaRef.current = null;
  };

  const onSubmit = async (data: UserFormValue) => {
    const isValid = await trigger();
    if (!isValid) return;

    const request = new UserRequest({
      codigo: data.codigo,
      nombre: data.nombre,
      mail: data.mail,
      tipoUsuario: data.tipoUsuario.value,
      cuenta: data.numeroCuenta,
      funcionalidades: data.funcionalidades,
      tarjetaId: data.tipoUsuario.value === TARJETA ? data.tarjetaId : null,
    });
    initialCuentaRef.current = data.numeroCuenta;
    saveUser(request);
  };

  if (isLoading) {
    return <UserFormSkeleton />;
  }

  return (
    <FormWithValidation onSubmit={handleSubmit(onSubmit)} reactFormHookMethods={methods}>
      <Card>
        <Card.Title>Datos del usuario</Card.Title>
        <Card.Body $padding>
          <Row className="mb-2">
            <Col>
              <FormWithValidation.TextInput
                name="codigo"
                label="Código"
                fullWidth
                registerOptions={{
                  required: FORM_ERROR_MESSAGES.fieldRequierd,
                }}
              />
            </Col>
            <Col>
              <FormWithValidation.TextInput
                name="mail"
                label="Email"
                fullWidth
                registerOptions={{
                  required: FORM_ERROR_MESSAGES.fieldRequierd,
                  validate: (text: string) =>
                    isEmail(text) || FORM_ERROR_MESSAGES.invaildEmail,
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <FormWithValidation.TextInput
                name="nombre"
                label="Nombre y apellido"
                fullWidth
                registerOptions={{
                  required: FORM_ERROR_MESSAGES.fieldRequierd,
                }}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Title>Informacion que puede consultar</Card.Title>
        <Card.Body $padding>
          <Row>
            <Col>
              <FormLabel>Tipo de usuario</FormLabel>
              <FormWithValidation.LegacySelect
                name="tipoUsuario"
                options={TIPO_USUARIO}
                onChange={handleChangeTipoUsuario}
                registerOptions={{
                  required: FORM_ERROR_MESSAGES.fieldRequierd,
                }}
              />
              <FormWithValidation.Control.Feedback
                type="invalid"
                controlId="tipoUsuario"
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <CuentaSelector
                label={tipoUsuario?.value === CENTRO_COSTO ? 'Centro de Costo' : 'Cuenta'}
                name="numeroCuenta"
                filtros={{
                  tipoCuenta:
                    tipoUsuario?.value === TARJETA || tipoUsuario?.value === CENTRO_COSTO
                      ? tipoUsuario?.value
                      : null,
                }}
                registerOptions={{
                  required: FORM_ERROR_MESSAGES.fieldRequierd,
                }}
              />
              <FormWithValidation.Control.Feedback
                type="invalid"
                controlId="numeroCuenta"
              />
            </Col>
          </Row>
          {tipoUsuario?.value === TARJETA && selectedCuenta && (
            <Row>
              <Col>
                <TarjetaSelector
                  name="tarjetaId"
                  filtros={{
                    numeroCuenta: selectedCuenta,
                    cuentasHuerfanas: false,
                  }}
                  registerOptions={{
                    required: FORM_ERROR_MESSAGES.fieldRequierd,
                  }}
                />
                <FormWithValidation.Control.Feedback
                  type="invalid"
                  controlId="tarjetaId"
                />
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
      <Card>
        <Card.Title>Funcionalidades</Card.Title>
        <Card.Body $padding>
          <FuncionalidadesSelector
            name="funcionalidades"
            tipoUsuario={tipoUsuario?.value}
            registerOptions={{
              required: FORM_ERROR_MESSAGES.atLeastOneOptionSelected,
            }}
          />
        </Card.Body>
        <Card.Footer>
          <Row>
            <Col>
              <Button className="float-end" type="submit">
                {userInfo ? 'Modificar' : 'Crear'}
              </Button>
            </Col>
          </Row>
        </Card.Footer>
      </Card>
    </FormWithValidation>
  );
}
